/**
 * Content Filter — Public API
 * 
 * Validates text content against prohibited word lists.
 * Used by bio updates, post creation, and message sending.
 * 
 * Usage:
 *   import { validateText, containsProhibitedContent } from '../utils/contentFilter';
 *   
 *   const result = validateText('some user text');
 *   if (!result.isClean) {
 *     // result.violations[0].label → "Hate speech"
 *     // result.message → user-friendly error string
 *   }
 */

import { PROHIBITED_CATEGORIES } from './prohibitedWords';
import { normalizeText, findViolations } from './matcher';
import { CONTENT_FILTER } from '../../constants/staticTexts';

/**
 * Strip HTML tags from rich text content (TipTap output).
 * Preserves text content only.
 */
function stripHtml(html) {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Validate text against all prohibited word categories.
 * 
 * @param {string} text - Raw text or HTML content to validate
 * @param {Object} [options] - Optional configuration
 * @param {boolean} [options.isHtml=false] - Whether the input is HTML (will strip tags first)
 * @param {string[]} [options.skipCategories] - Category keys to skip (e.g., ['SOLICITATION'])
 * @returns {{ isClean: boolean, violations: Array, message: string|null }}
 */
export function validateText(text, options = {}) {
  if (!text || typeof text !== 'string') {
    return { isClean: true, violations: [], message: null };
  }

  const { isHtml = false, skipCategories = [] } = options;

  // Strip HTML if needed
  const plainText = isHtml ? stripHtml(text) : text;

  // Normalize for matching
  const normalized = normalizeText(plainText);

  if (!normalized) {
    return { isClean: true, violations: [], message: null };
  }

  // Filter categories if skipCategories specified
  let categories = PROHIBITED_CATEGORIES;
  if (skipCategories.length > 0) {
    categories = Object.fromEntries(
      Object.entries(PROHIBITED_CATEGORIES).filter(
        ([key]) => !skipCategories.includes(key)
      )
    );
  }

  const violations = findViolations(normalized, categories);

  if (violations.length === 0) {
    return { isClean: true, violations: [], message: null };
  }

  // Build user-friendly message from the highest severity violation
  // Don't echo the prohibited word — only show category labels
  const criticalViolations = violations.filter(v => v.severity === 'critical');
  const highViolations = violations.filter(v => v.severity === 'high');

  const topViolation = criticalViolations[0] || highViolations[0];

  const message = CONTENT_FILTER.PROHIBITED_WORD_ERROR;

  return {
    isClean: false,
    violations,
    message,
    severity: topViolation.severity,
  };
}

/**
 * Quick check — returns true if text contains prohibited content.
 * 
 * @param {string} text - Text to check
 * @param {Object} [options] - Same options as validateText
 * @returns {boolean}
 */
export function containsProhibitedContent(text, options = {}) {
  return !validateText(text, options).isClean;
}
